import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCompanies, fetchCompanyDetails } from '../../Utils/apiServices';

/**
 * Normalize timestamps in company data to handle microseconds properly
 * @param {Object} data - Company data object that may contain timestamps
 * @returns {Object} - Normalized company data
 */
const normalizeCompanyData = (data) => {
  if (!data) return data;
  
  // Create a new object to avoid mutating the original
  const normalized = { ...data };
  
  // Normalize common timestamp fields
  const timestampFields = ['created_at', 'updated_at', 'last_login', 'expiry_date'];
  
  timestampFields.forEach(field => {
    if (normalized[field] && typeof normalized[field] === 'string') {
      try {
        // Handle ISO timestamps with microseconds by removing them
        if (normalized[field].includes('.')) {
          normalized[field] = normalized[field].split('.')[0] + 'Z';
        }
      } catch (error) {
        console.error(`Error normalizing timestamp field ${field}:`, error);
      }
    }
  });
  
  return normalized;
};

// Async thunk to fetch companies
export const getCompanies = createAsyncThunk(
  'company/getCompanies',
  async (_, { rejectWithValue }) => {
    try {
      const companies = await fetchCompanies();
      // Normalize each company in the array
      return Array.isArray(companies) 
        ? companies.map(company => normalizeCompanyData(company))
        : companies;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch company details
export const getCompanyDetails = createAsyncThunk(
  'company/getCompanyDetails',
  async (companyId, { rejectWithValue }) => {
    try {
      const company = await fetchCompanyDetails(companyId);
      // Normalize the company data
      return normalizeCompanyData(company);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  companies: [],
  selectedCompany: null,
  loading: false,
  error: null,
};

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {
    setSelectedCompany: (state, action) => {
      state.selectedCompany = action.payload;
    },
    clearSelectedCompany: (state) => {
      state.selectedCompany = null;
    },
    clearCompanies: (state) => {
      state.companies = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle getCompanies
      .addCase(getCompanies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanies.fulfilled, (state, action) => {
        state.loading = false;
        state.companies = action.payload;
      })
      .addCase(getCompanies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle getCompanyDetails
      .addCase(getCompanyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCompanyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCompany = action.payload;
      })
      .addCase(getCompanyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSelectedCompany, clearSelectedCompany, clearCompanies } = companySlice.actions;
export default companySlice.reducer; 